import { parseJwt, useAppleLogin as useAppleSdk } from '@myfunnow/web-core'
import { useThirdLogin } from '@/composables/useThirdLogin'
import { useLoginSdkInit } from '@/composables/useLoginSdkInit'
import * as loginService from '@/service/login'
import { ResponseCode } from '@/view-models/common'

interface AppleJwtPayload {
  email: string
}

export function useAppleLogin(options?: { onSuccess?: () => void }) {
  const nuxtApp = useNuxtApp()
  const { $axios, $store: store } = nuxtApp
  const config = useRuntimeConfig()

  const appleLoginLoading = ref(false)

  const { showConsent, pendingUserInfo, onLoginSuccess, onNeedRegister, handleConsentConfirm, openErrorDialog, resetConsent } = useThirdLogin({
    provider: 'apple',
    registerOptions: { byApple: true },
    onSuccess: () => {
      const email = pendingUserInfo.value?.email ?? ''
      clearStoredUser(email)
      options?.onSuccess?.()
    },
  })

  const { initialize, signIn, getStoredUser, clearStoredUser } = useAppleSdk({
    clientId: config.public.APPLE_AUTH_SERVICES_ID as string,
    redirectURI: config.public.APPLE_AUTH_REDIRECT_URI as string,
  })

  useLoginSdkInit(initialize)

  async function loginWithApple() {
    if (appleLoginLoading.value) return
    appleLoginLoading.value = true
    try {
      const response = await signIn()
      await loginFunNow(response)
    } catch (error: any) {
      // Silent ignore when user closes the popup
      if (error?.error === 'popup_closed_by_user') {
        return
      }
      openErrorDialog(error instanceof Error ? error.message : String(error))
    } finally {
      appleLoginLoading.value = false
    }
  }

  async function loginFunNow(response: AppleSignInAPI.SignInResponseI) {
    const { authorization } = response
    let email = ''
    try {
      const decoded = parseJwt<AppleJwtPayload>(authorization.id_token)
      email = decoded.email ?? ''
    } catch {
      // noop — email stays empty
    }
    try {
      const loginResult = await loginService.appleLogin($axios as any, {
        identityToken: authorization.id_token,
        authorizationCode: authorization.code,
      })
      clearStoredUser(email)
      onLoginSuccess(loginResult.data.uid)
      await store.dispatch('authentication/getUserInfo')
    } catch (error: any) {
      const err = error as { code?: number; message?: string }
      if (err.code === ResponseCode.TokenNotFound) {
        preparePendingUserInfo(authorization.id_token, authorization.code, email)
        return
      }
      openErrorDialog(err.message ?? '')
    }
  }

  function preparePendingUserInfo(idToken: string, authCode: string, email: string) {
    try {
      const appleUser = getStoredUser(email)
      const name = appleUser?.name ? [appleUser.name.firstName, appleUser.name.lastName].filter(Boolean).join(' ') : ''
      onNeedRegister({
        email,
        name,
        apple_token: idToken,
        apple_auth_code: authCode,
      })
    } catch (error: any) {
      console.error('preparePendingUserInfo: error', error)
      openErrorDialog(error instanceof Error ? error.message : String(error))
    }
  }

  return {
    loginWithApple,
    showConsent,
    pendingUserInfo,
    handleConsentConfirm,
    appleLoginLoading,
    resetConsent,
  }
}
